import React, { useEffect } from 'react';
import { useQuiz } from '../context/QuizContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { ProgressBar, Card, Button, Container, Row, Col } from 'react-bootstrap';
import quizData from '../data/quizzes.json';
import './Quiz.css';

function Quiz() {
  const { state, dispatch } = useQuiz();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const {
    currentState,
    currentCategory,
    currentLanguage,
    currentQuestion,
    answers,
    revealedAnswers,
    quizData: currentQuizData,
    isComplete
  } = state;

  // Load quiz data when component mounts or when state/category changes
  useEffect(() => {
    const data = quizData[currentState][currentCategory];
    dispatch({ type: 'SET_QUIZ_DATA', data });
  }, [currentState, currentCategory, dispatch]);

  if (!currentQuizData) {
    return <div role="alert" aria-busy="true">{t('quiz.loading')}</div>;
  }

  const question = currentQuizData.questions[currentQuestion];
  const totalQuestions = currentQuizData.questions.length;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;
  const isAnswerRevealed = revealedAnswers[question.id];
  const userAnswer = answers[question.id];
  const isAnswered = userAnswer !== undefined;
  const isCorrect = isAnswered && userAnswer === question.correctAnswer;

  const handleAnswer = (answer) => {
    dispatch({
      type: 'ANSWER_QUESTION',
      questionId: question.id,
      answer
    });
  };

  const handleRevealAnswer = () => {
    dispatch({
      type: 'REVEAL_ANSWER',
      questionId: question.id
    });
  };

  const handleBookmark = () => {
    dispatch({ type: 'BOOKMARK_QUESTION', questionId: question.id });
  };

  if (isComplete) {
    const score = (state.score / totalQuestions) * 100;    return (
      <Container className="py-4">
        <Card className="quiz-complete text-center">
          <Card.Body>            <Card.Title className="display-4 mb-4" role="status">{t('quiz.complete')}</Card.Title>
            <Card.Text 
              className="h2 mb-4" 
              role="status" 
              aria-live="polite"
              aria-atomic="true"
            >
              {t('quiz.yourScore')}: {score.toFixed(2)}%
            </Card.Text>
            <Button 
              variant="primary" 
              size="lg"
              onClick={() => dispatch({ type: 'RESET_QUIZ' })}
              aria-label={t('quiz.tryAgain')}
            >
              {t('quiz.tryAgain')}
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }
  const answeredQuestions = Object.keys(answers).length;
  const answeredProgress = (answeredQuestions / totalQuestions) * 100;

  return (
    <Container className="py-4">
      <Card className="quiz-container shadow-sm" role="main" aria-label="Quiz question">
        <Card.Header className="bg-light">
          <h2 className="h4 mb-3" id="quiz-title">{currentQuizData.name}</h2>
          <div className="progress-container">
            <ProgressBar 
              className="mb-2"
              aria-labelledby="quiz-progress"
              role="progressbar"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={progress}
            >
              <ProgressBar 
                variant="success" 
                now={answeredProgress} 
                key={1}                label={`${answeredQuestions}/${totalQuestions} ${t('quiz.answered')}`}
                aria-label={`${answeredQuestions} ${t('quiz.of')} ${totalQuestions} ${t('quiz.questionsAnswered')}`}
              />
              <ProgressBar 
                variant="info" 
                now={progress - answeredProgress} 
                key={2}
                aria-label={t('quiz.currentProgress')}
              />
            </ProgressBar>
            <small className="text-muted">
              {t('quiz.question')} {currentQuestion + 1} {t('quiz.of')} {totalQuestions}
            </small>
          </div>
        </Card.Header>        <Card.Body>
          <p 
            className="question-text h5 mb-4" 
            id={`question-${currentQuestion}`}
            role="heading" 
            aria-level="3"
          >
            {question.text[currentLanguage]}
          </p>
          <div 
            className="options-list d-grid gap-2"
            role="radiogroup"
            aria-labelledby={`question-${currentQuestion}`}
          >
            {question.options[currentLanguage].map((option, index) => {
              const isSelected = answers[question.id] === index;
              const isCorrectAnswer = index === question.correctAnswer;
              let variant = 'outline-primary';
              
              if (isAnswerRevealed || isSelected) {
                if (isCorrectAnswer) {
                  variant = 'success';
                } else if (isSelected && !isCorrectAnswer) {
                  variant = 'danger';
                }
              }

              return (
                <Button
                  key={index}
                  variant={variant}
                  className={`text-start p-3 ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleAnswer(index)}
                  disabled={isAnswerRevealed}
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={`Option ${index + 1}: ${option}`}
                >
                  {option}
                  {isAnswerRevealed && isCorrectAnswer && (
                    <span className="ms-2" role="img" aria-label="Correct answer">✓</span>
                  )}
                </Button>
              );
            })}
          </div>
          
          {isAnswered && (
            <div className={`mt-4 text-${isCorrect ? 'success' : 'danger'}`} role="alert">
              <p className="mb-2">
                {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
              </p>
            </div>
          )}

          {isAnswerRevealed && (
            <div className="mt-4 alert alert-info" role="alert">
              <h4 className="h5">Explanation:</h4>
              <p>{question.explanation[currentLanguage]}</p>
            </div>
          )}
          
          <div className="d-flex justify-content-between align-items-center mt-4">
            <Button
              variant="outline-secondary"
              onClick={handleBookmark}
              className="bookmark-button"
              aria-pressed={state.bookmarkedQuestions.includes(question.id)}
              aria-label={t('quiz.bookmark')}
            >
              <i className="bi bi-bookmark-fill me-2" aria-hidden="true"></i>
              {t('quiz.bookmark')}
            </Button>
            
            {!isAnswerRevealed && (
              <Button
                variant="outline-info"
                onClick={handleRevealAnswer}
                className="reveal-button"
                aria-label="Reveal the correct answer"
              >
                Reveal Answer
              </Button>
            )}
          </div>
        </Card.Body>        <Card.Footer className="bg-light">
          <Row className="align-items-center">
            <Col>
              {currentQuestion > 0 && (
                <Button
                  variant="outline-primary"
                  onClick={() => dispatch({ type: 'PREVIOUS_QUESTION' })}
                  aria-label={t('quiz.previousQuestion')}
                >
                  <span aria-hidden="true">←</span> {t('quiz.previous')}
                </Button>
              )}
            </Col>
            <Col className="text-center">
              <span 
                className="badge bg-secondary"
                role="status"
                aria-live="polite"
              >
                {t('quiz.question')} {currentQuestion + 1} {t('quiz.of')} {totalQuestions}
              </span>
            </Col>
            <Col className="text-end">
              {currentQuestion < totalQuestions - 1 && (
                <Button
                  variant="primary"
                  onClick={() => dispatch({ type: 'NEXT_QUESTION' })}
                  aria-label={t('quiz.nextQuestion')}
                >
                  {t('quiz.next')} <span aria-hidden="true">→</span>
                </Button>
              )}
            </Col>
          </Row>
        </Card.Footer>
      </Card>
    </Container>
  );
}

export default Quiz;
